import { faCircle, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Popover, Typography } from '@mui/material'
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'
import PromotionsApi from 'api/promotion/promotion.api'
import { showModal } from 'app/slice/modal.slice'
import { swalCustom } from 'component/Alert/Swal'
import HeaderCard from 'component/CardCustom/HeaderCard'
import Loading from 'component/Loading'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiSuccess } from 'component/notifications/notifications'
import { colors } from 'constants/theme'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
import { dateTimeToView } from 'utils/date.utils'
import { isCopy, isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'
import { exportPromotionsToExcel } from './exportPromotionsToExcel'
import PopViewPromotion from './pop-view-promotion'

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView(),
    isCopy: isCopy(),
}

const Promotions = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()
    const [load, setLoad] = useState<boolean>(false)
    const componentRef = useRef<HTMLDivElement>(null)

    const [dataPromotion, setDataPromotion] = useState<any>([])
    const [dataPromotionExport, setDataPromotionExport] = useState<any>([])

    const [statusSelect, setStatusSelect] = useState<string>('')
    const [branches, setBranches] = useState<BranchInterface[]>([])

    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('createdAt')
    const [sortType, setSortType] = useState('DESC')
    const [search, setSearch] = useState('')

    const [popViewPromotion, setPopViewPromotion] = useState<boolean>(false)
    const [dataPopViewPromotion, setDataPopViewPromotion] = useState<any>({})

    const loadFilterOption = async () => {
        await ClinicApi.findAllActiveBranches().then(({ data }) => data && setBranches(data))
    }

    const loadPromotion = async (checkExport?: boolean) => {
        setLoad(true)
        const condition = {
            page: page,
            pageLimit: pageLimit,
            SEARCH: search,
            sortBy: sortBy,
            sortType: sortType,
            STATUS: statusSelect === 'ALL' ? '' : statusSelect,
            EXPORT: checkExport ? '1' : '0'
        }

        await PromotionsApi.findAll(condition).then((res) => {
            if (checkExport) {
                setDataPromotionExport(res?.data)
            } else {
                setRowCount(res?.headers['x-total'] || 0)
                setDataPromotion(res?.data)
            }
        }).finally(() => {
            setLoad(false)
        })
        setLoad(false)
    }

    useEffect(() => {
        loadPromotion(true)
        loadFilterOption()
    }, [])

    useEffect(() => {
        loadPromotion()
    }, [page, pageLimit])

    const onCreate = () => {
        history.push(`${routeName.promotion}/create`)
    }

    const onEdit = (obj: any) => {
        history.push(`${routeName.promotion}/update`, { ...obj })
    }

    const onView = (obj: any) => {
        setPopViewPromotion(true)
        dispatch(showModal())
        setDataPopViewPromotion(obj)
    }

    const onClosePopViewPromotion = () => {
        setPopViewPromotion(false)
    }

    const onCopy = (obj: any) => {
        history.push(`${routeName.promotion}/create`, { ...obj, copy: true })
    }

    const onDelete = async (data: any) => {
        const { promotionNameEn, promotionName, promotionId } = data

        swalCustom(
            `<p class="mb-0 mx-auto" style="max-width: 325px">${t('PROMOTION.MESSAGE.DEL_TITLE')}</p>`,
            `<p class="mb-0 mx-auto" style="max-width: 325px">${t('PROMOTION.MESSAGE.DEL_SUB', { promotionName: promotionName, promotionNameEn: promotionNameEn })}</p>`,
            (res: any) => {
                if (res && promotionId) {
                    setLoad(true)
                    PromotionsApi.deletePromotion(promotionId).then((res) => {
                        notiSuccess(t('PROMOTION.MESSAGE.DEL_SUCCESS'))
                        loadPromotion()
                        loadPromotion(true)
                    }).finally(() => {
                        setLoad(false)
                    })
                }
            }, 'warning', { showCloseButton: false, containerClass: 'promotion-del', confirmButton: { confirmClassName: 'promotion-del-con' } }
        )

    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangePage = (val: number) => setPage(val)

    const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

    const headCells = [
        { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '50px', align: 'center', },
        { id: 'promotionTypeName', disablePadding: false, label: t('PROMOTION.TABLE.PROMOTION_TYPENAME'), align: 'left', class: 'text-nowrap ' },
        { id: 'promotionCode', disablePadding: false, label: t('PROMOTION.TABLE.PROMOTION_CODE'), align: 'center', class: 'text-nowrap' },
        { id: 'promotionName', disablePadding: false, label: t('PROMOTION.TABLE.PROMOTION_NAME'), align: 'left', class: 'text-nowrap col-pro-name' },
        { id: 'promotionNameEn', disablePadding: false, label: t('PROMOTION.TABLE.PROMOTION_NAMEEN'), align: 'left', class: 'text-nowrap' },
        { id: 'promotionFormat', disablePadding: false, label: t('PROMOTION.TABLE.TYPE'), align: 'center', class: 'text-nowrap' },
        { id: 'branch', disablePadding: false, label: t('PROMOTION.TABLE.USAGE_BRANCH'), align: 'left', class: 'text-nowrap' },
        { id: 'count', disablePadding: false, label: t('PROMOTION.TABLE.COUNT'), align: 'center', class: 'text-nowrap' },
        { id: 'dateStart', disablePadding: false, label: t('PROMOTION.TABLE.USAGE_DATE'), align: 'center', class: 'text-nowrap' },
        { id: 'status', disablePadding: false, label: t('APPLICATION.TABLE.CELL.STATUS'), align: 'center', width: '150px' },
        { id: 'updatedBy', disablePadding: false, label: t('APPLICATION.TABLE.CELL.UPDATED_BY'), align: 'center', width: '180px' },
        { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
    ]

    const renderData = (objData: any, index: number, isExport?: boolean) => {
        if (isExport) {
            index = index + 1
        } else {
            index = page * pageLimit - pageLimit + index + 1
        }

        const { promotionId, promotionCode, promotionName, promotionNameEn, promotionType,
            updatedBy, updatedAt, createdAt, status, branch, count, dateStart, dateEnd, amountPromotion, promotionBranchs, type, promotionDate } = objData

        const valueAction = (status === 'USING' || status === 'EXPIRED') ? [{ option: 'VIEW', label: t(`BUTTON.VIEW`), disabled: permissions.isView.disabled },
        { option: 'COPY', label: t(`BUTTON.COPY`), disabled: permissions.isCopy.disabled }]
            :
            [{ option: 'VIEW', label: t(`BUTTON.VIEW`), disabled: permissions.isView.disabled },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'COPY', label: t(`BUTTON.COPY`), disabled: permissions.isCopy.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }]

        const objRenderData = {
            key: promotionId,
            id: promotionId,
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: index },
                { option: 'TEXT', align: 'left', label: promotionType?.promotionTypeName || '' },
                { option: 'TEXT', align: 'left', label: promotionCode || '' },
                { option: 'TEXT', align: 'left', label: promotionName || '' },
                { option: 'TEXT', align: 'left', label: promotionNameEn || '' },
                { option: 'TEXT', align: 'center', label: type ? t(`PROMOTION.TYPE.${type}`) : '' },
                {
                    option: 'COMPONENT',
                    component: (!isExport ? <BoxBranchFilter filterBranch={promotionBranchs?.length ? promotionBranchs : []} /> : (<Box>{promotionBranchs[0]?.branchName || ''}</Box>))
                },
                { option: 'TEXT', align: 'center', label: amountPromotion || '' },
                { option: 'TEXT', align: 'center', label: promotionDate || '', class: 'text-nowrap' },
                {
                    option: 'COMPONENT',
                    class: 'row-status',
                    component: (
                        <Box className={'text-nowrap'}>
                            {!isExport && <FontAwesomeIcon icon={faCircle} color={status === 'USING' ? colors.statusActive : status === 'EXPIRED' ? colors.statusInactive : '#EDBA07'} />}
                            <span className="ml-2">{t(`PROMOTION.STATUS.${status}`)}</span>
                        </Box>
                    )
                },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || '-', updatedAt: updatedAt ?? '10/12/2564 08.59' } },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: valueAction
                },
            ]
        }

        if (isExport) {
            return <TableRowExport {...objRenderData} />
        }

        return (
            <TableRowCommon
                {...objRenderData}
                onedit={() => onEdit(objData)}
                ondelete={() => onDelete(objData)}
                onView={() => onView(objData)}
                oncopy={() => onCopy(objData)}
            />
        )
    }

    return (
        <Box>
            <Loading show={load} />
            <HeaderCard text={t('PROMOTION.TITLE')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            label={t('PROMOTION.SEARCH_PLACEHOLDER')}
                            value={search}
                            size='large'
                            fullWidth={true}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    setPage(1)
                                    loadPromotion()
                                }
                            }}
                            inputProps={{
                                value: search,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('')
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            value={statusSelect}
                            onChange={(event: any) => {
                                setStatusSelect(event.target.value)
                            }}
                            defaultValue={statusSelect}
                            label="สถานะ"
                            options={[
                                { name: t('PROMOTION.STATUS.ALL'), value: "ALL" },
                                { name: t(`PROMOTION.STATUS.WAITING`), value: "WAITING" },
                                { name: t(`PROMOTION.STATUS.USING`), value: "USING" },
                                { name: t(`PROMOTION.STATUS.EXPIRED`), value: "EXPIRED" }
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} xl={8}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <MainButton
                                title={t('PROMOTION.BUTTON.SEARCH')}
                                onClick={() => {
                                    setPage(1)
                                    loadPromotion()
                                    loadPromotion(true)
                                }}
                                type="primary"
                                variant="contained"
                                style={{ height: '40px', width: '100px' }}
                            />
                            <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                                <DropdownButton title={'พิมพ์เอกสาร'} onChange={(e) => {
                                    if (e === 'Excel') {
                                        let statusFilter = t('PROMOTION.STATUS.ALL')
                                        if (statusSelect === 'ALL') { statusFilter = t('PROMOTION.STATUS.ALL') }
                                        else if (statusSelect === 'WAITING') { statusFilter = t('PROMOTION.STATUS.WAITING') }
                                        else if (statusSelect === 'USING') { statusFilter = t('PROMOTION.STATUS.USING') }
                                        else if (statusSelect === 'EXPIRED') { statusFilter = t('PROMOTION.STATUS.EXPIRED') }
                                        const report: any = {
                                            title: t('PROMOTION.TITLE'),
                                            statusFilter: statusFilter,
                                            rows: dataPromotionExport.map((item: any) => {
                                                const branchName = (item.promotionBranchs || [])
                                                    .map((b: any) => b.branchName)
                                                    .join(', ') || '-'
                                                return {
                                                    promotionTypeName: item.promotionType?.promotionTypeName || '-',
                                                    promotionCode: item.promotionCode || '-',
                                                    promotionName: item.promotionName || '-',
                                                    promotionNameEn: item.promotionNameEn || '-',
                                                    promotionFormat: t(`PROMOTION.TYPE.${item.type}`) || '-',
                                                    branchName,
                                                    count: item.amountPromotion || 0,
                                                    usageDate: item?.promotionDate,
                                                    status: t(`PROMOTION.STATUS.${item.status}`),
                                                    updatedBy: item.updatedBy || '-',
                                                    updatedAt: dateTimeToView(item.updatedAt)
                                                }
                                            }),
                                            t
                                        }
                                        exportPromotionsToExcel(report)

                                    } else if (e === 'PDF') {
                                        const style = document.createElement("style");
                                        style.innerHTML = `
                                @media print {
                                    @page {size: landscape }
                                          }
                                `;
                                        document.head.appendChild(style);
                                        window.print();
                                        document.head.removeChild(style);

                                    }
                                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                                <MainButton
                                    onClick={() => onCreate()}
                                    title={t('PROMOTION.TITLE_CREATE')}
                                    type="primary"
                                    variant="contained"
                                    startIcon={<AddCircleIcon />}
                                    style={{ height: '40px' }}
                                    disabled={permissions.isCreate.disabled}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Box marginTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={dataPromotion?.length ? dataPromotion?.map((item: any, index: number) => {
                            return renderData(item, index)
                        }) : []}
                    />
                </Box>
            </Box>
            {popViewPromotion &&
                <PopViewPromotion
                    objData={dataPopViewPromotion}
                    onClose={onClosePopViewPromotion}
                />
            }
            <Box
                id="print-table-container"
                ref={componentRef}
            >
                <Typography fontSize="14px" fontWeight={400}>{t('PROMOTION.TITLE')}</Typography>
                <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                    <Typography fontSize="10px" fontWeight={400}>{t('STATUS.LABEL')}: {t(`PROMOTION.STATUS.${statusSelect || 'ALL'}`)}</Typography>
                </Box>
                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                        <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                            <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION.TABLE.PROMOTION_TYPENAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('PROMOTION.TABLE.PROMOTION_CODE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION.TABLE.PROMOTION_NAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION.TABLE.PROMOTION_NAMEEN')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('PROMOTION.TABLE.TYPE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION.TABLE.USAGE_BRANCH')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('PROMOTION.TABLE.COUNT')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION.TABLE.USAGE_DATE')}</th>
                            <th style={{ borderBottom: '1px solid black' }}>{t('APPLICATION.TABLE.CELL.STATUS')}</th>
                            <th style={{ borderBottom: '1px solid black' }}>{t('APPLICATION.TABLE.CELL.UPDATED_BY')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dataPromotionExport?.length === 0 &&
                            <tr>
                                <td colSpan={11} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                            </tr>
                        }
                        {dataPromotionExport.map((data: any, index: number) => (
                            <tr key={index}>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '100px' }}>{data?.promotionType?.promotionTypeName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{data?.promotionCode || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '150px' }}>{data?.promotionName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '150px' }}>{data?.promotionNameEn || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100px' }}>{t(`PROMOTION.TYPE.${data?.type}`) || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '120px' }}>{data?.promotionBranchs?.[0]?.branchName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{data?.amountPromotion || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '130px' }}>{data?.promotionDate || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{data?.status === "USING" ? 'กำลังใช้งาน' : data?.status === 'WAITING' ? 'กำลังใช้งาน' : 'หมดอายุ' || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <span>{data?.updatedBy || '-'}</span>
                                    <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    )
}

export default Promotions

type BoxBranchFilterProps = {
    filterBranch: BranchInterface[]
}

const BoxBranchFilter = (props: BoxBranchFilterProps) => {
    const { filterBranch } = props
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const total = filterBranch?.length ?? 0
    const firstName = filterBranch[0]?.branchName || '-'
    const restCount = total - 1
    const open = Boolean(anchorEl)
    const ITEM_HEIGHT = 32
    const VISIBLE_ITEMS = 15

    // สาขาเดียว → text ธรรมดา ไม่มี badge
    if (total <= 1) return <Box>{firstName}</Box>
    return (
        <>
            <Box
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                <span>{firstName}</span>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '22px',
                        height: '20px',
                        px: '6px',
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#5A6271',
                        bgcolor: '#F2F4F7',
                        border: `1px solid ${colors.lightGray}`,
                        borderRadius: '4px',
                    }}
                >
                    +{restCount}
                </Box>
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        borderRadius: 1,
                        border: `1px solid ${colors.lightGray}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,.08)',
                        maxHeight: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
                        overflowY: 'auto',
                        minWidth: 220,
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: colors.lightGray, borderRadius: 3 },
                    }
                }}
            >
                <Box sx={{ py: 0.5 }}>
                    {filterBranch.map((item, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                height: `${ITEM_HEIGHT}px`,
                                lineHeight: `${ITEM_HEIGHT}px`,
                                px: 1.5,
                                fontSize: '13px',
                                whiteSpace: 'nowrap',
                                color: colors.textPrimary,
                                borderBottom: idx < filterBranch.length - 1 ? `1px solid ${colors.lightGray}` : 'none',
                                '&:hover': { bgcolor: colors.themeSecondColor06 },
                            }}
                        >
                            {item?.branchName}
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    )
}